type ApiError = {
  message?: string;
  code?: string;
  response?: {
    data?: {
      message?: string;
      msg?: string;
      errors?: unknown;
    };
    status?: number;
  };
  request?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatValidationErrors = (errors: unknown): string | null => {
  if (typeof errors === 'string') {
    return errors;
  }

  if (Array.isArray(errors)) {
    const messages = errors.filter((item): item is string => typeof item === 'string');
    return messages.length > 0 ? messages.join(', ') : null;
  }

  if (isRecord(errors)) {
    const messages = Object.values(errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((item): item is string => typeof item === 'string');

    return messages.length > 0 ? messages.join(', ') : null;
  }

  return null;
};

export const isNetworkError = (error: unknown): boolean => {
  const err = error as ApiError;
  return (
    err?.code === 'ERR_NETWORK' ||
    err?.message === 'Network Error' ||
    (!err?.response && Boolean(err?.request))
  );
};

export const getErrorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  if (!error) {
    return fallback;
  }

  if (typeof error === 'string') {
    return error;
  }

  const err = error as ApiError;
  const responseMessage = err.response?.data?.message || err.response?.data?.msg;
  if (responseMessage) {
    return String(responseMessage);
  }

  const validationMessage = formatValidationErrors(err.response?.data?.errors);
  if (validationMessage) {
    return validationMessage;
  }

  if (isNetworkError(error)) {
    return 'Network Error';
  }

  if (typeof err.message === 'string' && err.message.trim()) {
    return err.message;
  }

  return fallback;
};
